import { useEffect } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { memberSocialPosts } from '@urls';
import { edgesToProducts } from '@utils/products';
import { useFilterData } from '@hook/productFilters';
import { useViewer } from '@hook/useViewer';
import { sendSellerShopIntentEvent } from '@utils/analytics';
import { ProfileProductsFeed, ProfileProductsFeedSkeleton } from '@components/ProfileProductsFeed';

import { useProfileProductsFeedQuery } from './gql/graphql.v3.generated';
import { useProfileProductsFeedSocialPostsQuery } from './gql/graphql.v5.generated';

interface Props {
  username: string,
}

export const ProfileProductsFeedContainer: React.FC<Props> = ({
  username,
}) => {
  const history = useHistory();
  const { filters } = useFilterData();

  const {
    viewer,
  } = useViewer();

  const {
    data,
    loading,
    fetchMore,
  } = useProfileProductsFeedQuery({
    variables: {
      first: 18,
      username,
      ...filters,
      ukrainian: Boolean(filters.ukrainian),
    },
    context: {
      batch: true,
      ignoreAuthError: true,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const { data: postsData } = useProfileProductsFeedSocialPostsQuery({
    variables: {
      first: 1,
      username,
    },
    context: {
      v5: true,
      batch: true,
    },
    fetchPolicy: 'no-cache',
  });

  const user = data?.user;
  const userProducts = user?.products;
  const pageInfo = userProducts?.pageInfo;
  const userPosts = postsData?.user?.posts;

  const isOwner = !!user && !!viewer && user?.id === viewer?.id;

  const handleLoadMore = async () => {
    if (pageInfo?.hasNextPage) {
      await fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (!isOwner) {
      sendSellerShopIntentEvent();
    }
  }, [isOwner]);

  useEffect(() => {
    if (!isOwner && userPosts?.edges.length && !loading && !userProducts?.edges.length) {
      history.replace(generatePath(memberSocialPosts, { username }));
    }
  }, [history, isOwner, userPosts, userProducts, loading, username]);

  if (loading) {
    return (
      <ProfileProductsFeedSkeleton />
    );
  }

  if (userProducts && pageInfo) {
    const products = edgesToProducts(userProducts?.edges);

    const relinkingCatalogs = user?.items.flatMap(
      (catalog) => catalog.subItems?.map((subCatalog) => ({ ...subCatalog, slug: `/${catalog.slug}/${subCatalog.slug}` })) || [],
    ).sort((l, r) => r.productsCount - l.productsCount).slice(0, 4);

    return (
      <ProfileProductsFeed
        products={products}
        hasNextPage={pageInfo.hasNextPage}
        total={pageInfo.total || 0}
        catalogs={user.items}
        sizeCatalogSlug={userProducts.mostFrequentCatalogSlug}
        relinkingCatalogs={relinkingCatalogs}
        city={user.city}
        onLoadMore={handleLoadMore}
      />
    );
  }

  return null;
};
